"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  clientDel,
  clientGet,
  clientPatch,
  clientPost,
} from "@/service/client-api/client-api";
import { CartDrawer } from "@/components/shop/cart-drawer";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WesubCartContextValue {
  isOpen: boolean;
  cart: WesubCart | null;
  isLoading: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (externalProductId: string, quantity?: number) => Promise<void>;
  removeItem: (externalProductId: string) => Promise<void>;
  updateQuantity: (
    externalProductId: string,
    quantity: number,
  ) => Promise<void>;
  clearCart: () => Promise<void>;
  refetchCart: () => Promise<void>;
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeCartResponse(payload: any): WesubCart {
  const sourceItems = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.content)
        ? payload.content
        : [];

  return {
    items: sourceItems
      .map((item: any) => {
        const externalProductId =
          item?.externalProductId ?? item?.product?.clientProductId ?? "";

        if (!externalProductId || !item?.product) {
          return null;
        }

        return {
          externalProductId: String(externalProductId),
          quantity: Math.max(1, toNumber(item?.quantity, 1)),
          product: item.product as WesubProduct,
        } satisfies WesubCartItem;
      })
      .filter(Boolean) as WesubCartItem[],
  };
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const WesubCartContext = createContext<WesubCartContextValue | null>(
  null,
);

export function useWesubCart(): WesubCartContextValue {
  const ctx = useContext(WesubCartContext);
  if (!ctx) {
    throw new Error("useWesubCart must be used inside WesubCartProvider");
  }
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function WesubCartProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState<WesubCart | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const data = await clientGet("wesub_cart.get");
      setCart(normalizeCartResponse(data));
    } catch {
      // unauthenticated or empty — leave cart null
      setCart({ items: [] });
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback(
    async (externalProductId: string, quantity = 1) => {
      setIsLoading(true);
      try {
        await clientPost("wesub_cart.add", {}, { externalProductId, quantity });
        await fetchCart();
        setIsOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCart],
  );

  const removeItem = useCallback(
    async (externalProductId: string) => {
      setIsLoading(true);
      try {
        await clientDel("wesub_cart.remove", { externalProductId });
        await fetchCart();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCart],
  );

  const updateQuantity = useCallback(
    async (externalProductId: string, quantity: number) => {
      setIsLoading(true);
      try {
        await clientPatch(
          "wesub_cart.update_quantity",
          { externalProductId },
          { quantity },
        );
        await fetchCart();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchCart],
  );

  const clearCart = useCallback(async () => {
    setIsLoading(true);
    try {
      await clientPost("wesub_cart.clear");
      await fetchCart();
    } finally {
      setIsLoading(false);
    }
  }, [fetchCart]);

  return (
    <WesubCartContext.Provider
      value={{
        isOpen,
        cart,
        isLoading,
        openCart,
        closeCart,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        refetchCart: fetchCart,
      }}
    >
      {children}
      <CartDrawer />
    </WesubCartContext.Provider>
  );
}
